import type { PcMonitorListing } from '../../types/pcmonitor.ts';
import { formatChf, listingValueGap, listingValueGapUnavailable } from './listingMath.ts';

interface ValueGapProps {
  listing: PcMonitorListing;
}

/**
 * The value gap (estimated_value − asking_price) as its own column — the
 * default sort key. An unresolved asking price (CHO-698) renders as a
 * visible badge, never a number, and no gap is computed. A resolved price
 * with no estimate is a different case and gets its own label — never
 * "Price unresolved".
 */
export default function ValueGap({ listing }: ValueGapProps) {
  const gap = listingValueGap(listing);
  if (gap === null) {
    if (listingValueGapUnavailable(listing) === 'unresolved_price') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
          Price unresolved
        </span>
      );
    }
    return <span className="text-xs text-gray-400">No estimate</span>;
  }
  const positive = gap.gap > 0;
  const sign = positive ? '+' : gap.gap < 0 ? '−' : '';
  return (
    <span className={`font-semibold tabular-nums ${positive ? 'text-emerald-700' : 'text-gray-500'}`}>
      {sign}
      {formatChf(Math.abs(gap.gap))}
      <span className={`ml-1 text-xs font-normal ${positive ? 'text-emerald-600' : 'text-gray-400'}`}>
        ({positive ? '+' : ''}
        {gap.gapPct}%)
      </span>
    </span>
  );
}
